import { useCallback, useEffect, useState } from 'react';

import { getDealBySlug, type DealWithPhotos } from '@/lib/db/deals';
import { addSavedDeal, isDealSaved, removeSavedDeal, SavedDealCapError } from '@/lib/db/buyer';

// Hook state pattern mirrors `useCatalogueDeals` / `useCatalogueStats` from
// DealsPage.data.ts: a discriminated union over `status` so the page can
// render skeleton / 404 / success without untyped boolean flags.

export type DealBySlugState =
  | { status: 'loading'; deal: null; error: null }
  | { status: 'notFound'; deal: null; error: null }
  | { status: 'success'; deal: DealWithPhotos; error: null }
  | { status: 'error'; deal: null; error: Error };

const INITIAL_DEAL: DealBySlugState = { status: 'loading', deal: null, error: null };

/**
 * `useDealBySlug` — fetches a single public deal by URL slug. Returns
 * `notFound` when Supabase returns no row (RLS hides draft / archived /
 * refused / expired / deleted deals from anon — Matrice §M4 L249), so the
 * page can `<Navigate to="/404" />` without a separate boolean check.
 *
 * Cancellation flag matches the existing pattern in `useCatalogueDeals` —
 * when the slug changes mid-flight, the stale response is dropped instead
 * of overwriting the fresh state.
 */
export function useDealBySlug(slug: string | undefined): DealBySlugState {
  const [state, setState] = useState<DealBySlugState>(INITIAL_DEAL);

  useEffect(() => {
    if (!slug) {
      setState({ status: 'notFound', deal: null, error: null });
      return;
    }

    let cancelled = false;
    setState(INITIAL_DEAL);

    getDealBySlug(slug)
      .then((deal) => {
        if (cancelled) return;
        if (deal === null) {
          setState({ status: 'notFound', deal: null, error: null });
        } else {
          setState({ status: 'success', deal, error: null });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          deal: null,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return state;
}

// ---------------------------------------------------------------------------
// useDealSave — initial saved state + optimistic toggle (BO-6 sub-lot C, §M4)
// ---------------------------------------------------------------------------
// Drives the Save / Unsave action on `<DealPage>`. The hook reads the initial
// state with `isDealSaved` at mount, then `toggle()` flips it via add/remove
// and rolls back on failure (e.g. cap reached). Returns a `null` userId / deal
// safe default so the page can call the hook even when auth is still loading.
//
// `toggle()` resolves to the new saved state, or rejects with the underlying
// error (SavedDealCapError caught by the page for its localised toast).

export type DealSaveState =
  | { status: 'loading'; saved: false }
  | { status: 'idle'; saved: boolean }
  | { status: 'toggling'; saved: boolean };

export function useDealSave(
  dealId: string | null,
  userId: string | null,
): { state: DealSaveState; toggle: () => Promise<boolean> } {
  const [state, setState] = useState<DealSaveState>({ status: 'loading', saved: false });

  useEffect(() => {
    if (!dealId || !userId) {
      setState({ status: 'idle', saved: false });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading', saved: false });
    isDealSaved({ userId, dealId })
      .then((saved) => {
        if (cancelled) return;
        setState({ status: 'idle', saved });
      })
      .catch(() => {
        if (cancelled) return;
        // Network failure → assume "not saved" so the user can retry from a
        // sane default; the next toggle will surface the underlying error.
        setState({ status: 'idle', saved: false });
      });
    return () => {
      cancelled = true;
    };
  }, [dealId, userId]);

  const toggle = useCallback(async (): Promise<boolean> => {
    if (!dealId || !userId) return false;
    if (state.status !== 'idle') return state.saved;
    const wasSaved = state.saved;
    // Optimistic update — flip the visible heart immediately.
    setState({ status: 'toggling', saved: !wasSaved });
    try {
      if (wasSaved) {
        await removeSavedDeal({ userId, dealId });
      } else {
        await addSavedDeal({ userId, dealId });
      }
      setState({ status: 'idle', saved: !wasSaved });
      return !wasSaved;
    } catch (err) {
      // Rollback to the previous state; re-throw so the page can show the
      // cap-reached toast (SavedDealCapError) or a generic one.
      setState({ status: 'idle', saved: wasSaved });
      if (err instanceof SavedDealCapError) throw err;
      throw err instanceof Error ? err : new Error(String(err));
    }
  }, [state, dealId, userId]);

  return { state, toggle };
}
